import { useQuery as useProductReviewsQuery } from "@faire/web-api/api/product/productToken/brand-reviews-summary/get-hooks";
import { useQuery as useProductRelatedSearchesQuery } from "@faire/web-api/api/seo/product-related-searches/productToken/get-hooks";
import { useEffect, useMemo } from "react";

import {
  buildBrandReviewSummaryFromProductBrandReview,
  buildProductReviewSummaryFromProductBrandReview,
} from "@faire/retailer/lib/buildReviewSummary";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { useSettingProductPageReactQuery } from "@faire/retailer/settings/useSettingProductPageReactQuery";
import { useLeanProductQuery } from "@faire/retailer/views/product/utils/useLeanProductQuery";

export const useProductState = (productToken: string) => {
  const productPageReactQuerySetting = useSettingProductPageReactQuery();
  const {
    data: resolvedProductResponse,
    dataUpdatedAt: clientProductResponseDataUpdatedAt,
    isLoading,
  } = useLeanProductQuery(productToken, {
    refetchOnMount: false,
    enabled: productPageReactQuerySetting,
  });

  const { data: reviewsSummary } = useProductReviewsQuery(productToken, {
    refetchOnMount: false,
    enabled: productPageReactQuerySetting,
  });

  const { data: productRelatedSearches } = useProductRelatedSearchesQuery(
    productToken,
    {
      refetchOnMount: false,
      enabled: productPageReactQuerySetting,
    }
  );

  const productReviewSummary = useMemo(() => {
    return buildProductReviewSummaryFromProductBrandReview(reviewsSummary);
  }, [reviewsSummary]);

  const brandReviewSummary = useMemo(() => {
    return buildBrandReviewSummaryFromProductBrandReview(reviewsSummary);
  }, [reviewsSummary]);

  const showroomData = useMemo(() => {
    return {
      isMidPricePointApparelProduct:
        resolvedProductResponse?.product?.purchase_information
          ?.is_mid_price_point_brand_apparel_product,
      showVerticalImagery:
        resolvedProductResponse?.product?.visuals?.show_vertical_image,
    };
  }, [resolvedProductResponse]);

  const samplesBannerDetails = useMemo(() => {
    if (
      !resolvedProductResponse?.product?.brand_sample_program
        ?.is_samples_enabled_for_session ||
      !resolvedProductResponse?.product?.brand_sample_program?.banner
    ) {
      return;
    }
    const { title, message, button_text, cta_link, cta_text } =
      resolvedProductResponse.product.brand_sample_program.banner;
    if (
      title === undefined ||
      message === undefined ||
      button_text === undefined ||
      cta_link === undefined ||
      cta_text === undefined
    ) {
      return;
    }
    return {
      title,
      message,
      buttonText: button_text,
      ctaLink: cta_link,
      ctaText: cta_text,
    };
  }, [resolvedProductResponse]);

  useEffect(() => {
    if (resolvedProductResponse && productPageReactQuerySetting) {
      PerformanceMarker.get().mark(PageLoadEvent.primaryFetchEnd);
    }
  }, [productPageReactQuerySetting, resolvedProductResponse]);

  return {
    resolvedProductResponse,
    clientProductResponseDataUpdatedAt,
    isLoading,
    reviewsSummary,
    productRelatedSearches: productRelatedSearches?.queries,
    productReviewSummary,
    brandReviewSummary,
    brand: resolvedProductResponse?.brand,
    product: resolvedProductResponse?.product,
    isBrandActive: !!resolvedProductResponse?.brand?.active,
    disclaimers: resolvedProductResponse?.product?.messages?.disclaimers ?? [],
    videos: resolvedProductResponse?.product?.visuals?.videos ?? [],
    showroomData,
    badges: resolvedProductResponse?.product?.visuals?.badges,
    importShipmentInfo:
      resolvedProductResponse?.product?.purchase_information
        ?.import_shipment_info,
    infoTexts: resolvedProductResponse?.product?.messages?.info_texts,
    productAttributeTags:
      resolvedProductResponse?.product?.details?.attribute_tags,
    samplesBannerDetails,
  };
};
